import {useChildShopId, useConfig, useShopId} from '@aboutyou/config';
import {GrpcClientProvider, GrpcConfig} from '@aboutyou/data-fetcher';
import {AB_TEST_HEADERS, useIsFeatureActive} from '@aboutyou/feature-flags';
import React, {useMemo, useState} from 'react';
import {useIsCouponsEnabled} from '../features/Coupons/useIsCouponsEnabled';
import {useIsWebCouponsEnabled} from '../features/Coupons/useIsWebCouponsEnabled';

const nonCriticalErrorMessagePatterns: RegExp[] = [
  /timeout exceeded fetching CRM placements/,
  /no target group with any products found/,
];

export const GrpcClientHostConfigProvider: React.FC<
  React.PropsWithChildren<{config: GrpcConfig}>
> = ({config, children}) => {
  const {tadaridaUrl: hostname, gripsUrl: legacyHostname, device} = useConfig();
  const shopId = useShopId();
  const childShopId = useChildShopId();

  const grpcSettings = useMemo(
    () => ({
      debug: false,
      enableDevTools: Boolean(process.browser),
      hostname,
      legacyHostname,
    }),
    [hostname, legacyHostname],
  );

  const legacyConfig = useMemo(
    () => ({
      device,
      shopId,
      childShopId,
    }),
    [device, shopId, childShopId],
  );

  const [abTestsConfig, setAbTestsConfig] = useState(config.abTests);

  const isWebCouponActive = useIsWebCouponsEnabled();
  const isCouponActive = useIsCouponsEnabled();
  const isMovCouponActive = useIsFeatureActive('mov_coupons').isActive;
  const isHtcCouponActive = useIsFeatureActive('high_tier_coupons').isActive;

  // Those are a temporary overrides to get the abTest config to all BE requests
  // we are doing this because there is no real ab test for this and the BE is using this `ab_coupons_rollout` and `ab_high_tier_coupons` abTest as a feature flag
  // this can be removed once the rollout is finalized
  const couponAbTestsOverride =
    isWebCouponActive || isCouponActive
      ? [{key: 'ab_coupons_rollout', group: 'cour-1'}]
      : [];

  if (isMovCouponActive) {
    couponAbTestsOverride.push({key: 'ab_enable_mov_coupons', group: 'emc-1'});
  }

  if (isHtcCouponActive) {
    couponAbTestsOverride.push({key: 'ab_high_tier_coupons', group: 'htc-1'});
  }

  const temporaryMergedConfig = {
    ...config,
    abTests: [...abTestsConfig, ...couponAbTestsOverride].filter(Boolean),
  } as GrpcConfig;

  return (
    <GrpcClientProvider
      config={temporaryMergedConfig}
      legacyConfig={legacyConfig}
      settings={grpcSettings}
      nonCriticalErrorMessagePatterns={nonCriticalErrorMessagePatterns}
      abTestOptions={{
        abTestHeaderName: AB_TEST_HEADERS.TadaridaConsideredABTests,
      }}
      updateAbTestConfig={setAbTestsConfig}
    >
      {children}
    </GrpcClientProvider>
  );
};

export const GrpcClientHostConfigProviderAppCheckout: React.FC<
  React.PropsWithChildren<{config: GrpcConfig}>
> = ({config, children}) => {
  const {tadaridaUrl: hostname, gripsUrl: legacyHostname, device} = useConfig();
  const shopId = useShopId();
  const childShopId = useChildShopId();

  const grpcSettings = useMemo(
    () => ({
      debug: false,
      enableDevTools: Boolean(process.browser),
      hostname,
      legacyHostname,
    }),
    [hostname, legacyHostname],
  );

  const legacyConfig = useMemo(
    () => ({
      device,
      shopId,
      childShopId,
    }),
    [device, shopId, childShopId],
  );

  return (
    <GrpcClientProvider
      config={config}
      legacyConfig={legacyConfig}
      settings={grpcSettings}
    >
      {children}
    </GrpcClientProvider>
  );
};
